import { RcToastPropsExtended, selectBreeds } from '@app/store/core';
import { Injectable } from '@angular/core';
import { AlertNotification, Breed, ProgressBarGroup } from '@app/core/models';
import { Tool } from '@app/shared/utils';
import { select, Store } from '@ngrx/store';
import { AppState } from '..';
import {
  getLocalization,
  removeAlert,
  resetAlerts,
  removeSuccessAlerts,
  setAlert,
  setBreeds,
  setPrevTool,
  setTool,
  toggleLoader,
  togglePopinLoader,
  toggleProgressBar,
  setStepperActiveStep,
  removeToast,
  setToast,
} from './core.actions';
import {
  selectAlerts,
  selectBCSLanguages,
  selectCurrentRoute,
  selectLoaderTransparent,
  selectRouteParams,
  selectShowLoader,
  selectShowPopinLoader,
  selectShowProgressBar,
  selectStepperItems,
  selectStepperItemsLegacy,
  selectToasts,
  selectToolInfo,
  selectUrl,
} from './core.selectors';
import { RCAlertType } from '@rc/ui';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { map, Observable, withLatestFrom } from 'rxjs';
import { getProgressGroupsFromStepperItems } from '@app/core/services/utils/progress-helper';
import { selectIsFeatureEnabled } from '../vet';

@Injectable({
  providedIn: 'root',
})
export class CoreFacade {
  currentRoute$ = this.store$.pipe(select(selectCurrentRoute));
  routeParams$ = this.store$.pipe(select(selectRouteParams));
  url$ = this.store$.pipe(select(selectUrl));

  showLoader$ = this.store$.pipe(select(selectShowLoader));
  showPopinLoader$ = this.store$.pipe(select(selectShowPopinLoader));
  loaderTransparent$ = this.store$.pipe(select(selectLoaderTransparent));
  showProgressBar$ = this.store$.pipe(select(selectShowProgressBar));
  bcsLanguages$ = this.store$.pipe(select(selectBCSLanguages));
  toolInfo$ = this.store$.pipe(select(selectToolInfo));
  breeds$ = this.store$.pipe(select(selectBreeds));
  alerts$ = this.store$.pipe(select(selectAlerts));
  toasts$ = this.store$.pipe(select(selectToasts));
  stepperItems$ = this.store$.pipe(select(selectStepperItems)).pipe(
    withLatestFrom(this.store$.pipe(select(selectToolInfo)), this.store$.pipe(select(selectIsFeatureEnabled('VS_RRV2')))),
    map(([stepperItems, { tool }, isRRV2Enabled]) => {
      if (tool === Tool.RapidReco && !isRRV2Enabled) return [];
      return stepperItems;
    }),
  );

  private readonly stepperItemsLegacy$ = this.store$.pipe(select(selectStepperItemsLegacy)).pipe(
    withLatestFrom(this.store$.pipe(select(selectToolInfo)), this.store$.pipe(select(selectIsFeatureEnabled('VS_RRV2')))),
    map(([stepperItemsLegacy, { tool }, isRRV2Enabled]) => {
      if (tool === Tool.RapidReco && isRRV2Enabled) return [];
      return stepperItemsLegacy;
    }),
  );
  progressValue$: Observable<ProgressBarGroup[]> = this.stepperItemsLegacy$.pipe(
    map((stepperItemsLegacy) => {
      if (!stepperItemsLegacy?.length) return [];
      return getProgressGroupsFromStepperItems(stepperItemsLegacy);
    }),
  );

  get hideSubHeader(): boolean {
    return document.body.classList.contains('hideSubHeader');
  }

  constructor(private readonly store$: Store<AppState>) {}

  /**
   * Toggle loader
   */
  toggleLoader(value: boolean, transparent?: boolean): void {
    this.store$.dispatch(toggleLoader({ value, transparent }));
  }

  /**
   * Toggle popin loader
   */
  togglePopinLoader(value: boolean): void {
    this.store$.dispatch(togglePopinLoader({ value }));
  }

  /**
   * Toggle progress bar
   */
  toggleProgressBar(value: boolean): void {
    this.store$.dispatch(toggleProgressBar({ value }));
  }

  /**
   * Load localization
   */
  loadLocalization(): void {
    this.store$.dispatch(getLocalization());
  }

  /**
   * Set tool
   */
  setTool(tool: Tool, tool_flow_id: string): void {
    this.store$.dispatch(setTool({ tool, tool_flow_id }));
  }

  /**
   * Set prev tool
   */
  setPrevTool(prevTool: Tool | null): void {
    this.store$.dispatch(setPrevTool({ prevTool }));
  }

  /**
   * Set breeds
   */
  setBreeds(breeds: Breed[]): void {
    this.store$.dispatch(setBreeds({ breeds }));
  }

  /**
   * Set alert
   */
  setAlert(alert: AlertNotification): void {
    this.store$.dispatch(setAlert({ alert }));
  }

  /**
   * Remove alert
   */
  removeAlert(alert: { alertType: RCAlertType; message: string }): void {
    this.store$.dispatch(removeAlert({ alert }));
  }

  /**
   * Remove all alerts
   */
  resetAlerts(ids: string[] = []): void {
    this.store$.dispatch(resetAlerts({ ids }));
  }

  /**
   * Remove all success alerts
   */
  removeSuccessAlerts(): void {
    this.store$.dispatch(removeSuccessAlerts());
  }

  /**
   * Set stepper's active index
   */
  setStepperActiveStep(index: number) {
    this.store$.dispatch(setStepperActiveStep({ index }));
  }

  handleSpeciesChangeWarningAlert(show: boolean): void {
    const alert = { message: translateKey('Species-change-warning'), alertType: RCAlertType.WARNING };

    if (show) {
      this.setAlert(alert);
    } else {
      this.removeAlert(alert);
    }
  }

  /**
   * Set toast
   */
  setToast(toast: RcToastPropsExtended): void {
    this.store$.dispatch(setToast({ toast }));
  }

  /**
   * Remove toast
   */
  removeToast(id: string): void {
    this.store$.dispatch(removeToast({ id }));
  }
}
